import React from 'react';
import {
  File,
  FileText,
  FileArchive,
  FileAudio,
  FileCode,
  FileVideo,
  Image,
  FileSpreadsheet,
  FolderOpen,
} from '@/shared/ui/icons';
import type { RoleAccess } from './documentPermissionUtils';
import type { GoogleDriveFile } from '../../types/documents';
import { FOLDER_MIME_TYPE } from './documentConstants';
import { documentScopeAdapter } from './documentScope';

export const mergeUniqueDocumentPages = (
  existing: readonly GoogleDriveFile[],
  incoming: readonly GoogleDriveFile[]
): GoogleDriveFile[] => {
  const merged: GoogleDriveFile[] = [];
  const seen = new Set<string>();

  for (const file of [...existing, ...incoming]) {
    const key = file.name || file.id;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(file);
  }

  return merged;
};

export type DocumentFolderRequestIdentity = {
  folderId: string;
  generation: number;
  paginated: boolean;
};

export const isSameDocumentFolderRequest = (
  active: DocumentFolderRequestIdentity | null,
  requested: DocumentFolderRequestIdentity
): boolean =>
  active?.folderId === requested.folderId &&
  active.generation === requested.generation &&
  active.paginated === requested.paginated;

export { ROLE_FOLDER_ACCESS } from './roleFolderAccess';

export const FOLDER_PARENT_MAP: Record<string, string> = {
  tenderDocuments: 'preContract',
  tenderReview: 'preContract',
  clarifications: 'preContract',
  estimating: 'preContract',
  takeOffs: 'preContract',
  quotes: 'preContract',
  pricing: 'preContract',
  tenderSubmission: 'preContract',
  executedContract: 'contractCompliance',
  insurancesBonds: 'contractCompliance',
  changeManagement: 'commercial',
  variationInstructions: 'commercial',
  valuationsVariations: 'commercial',
  claims: 'commercial',
  evidence: 'commercial',
  applicationsValuations: 'commercial',
  certificatesNotices: 'commercial',
  costReporting: 'commercial',
  statementsReconciliations: 'commercial',
  referenceDocs: 'operations',
  drawings: 'operations',
  scopeSpecs: 'operations',
  programme: 'operations',
  keyContacts: 'operations',
  safeSystems: 'operations',
  rams: 'operations',
  itps: 'operations',
  dailyRecords: 'operations',
  siteDiary: 'operations',
  progressPhotos: 'operations',
  toolboxTalks: 'operations',
  permits: 'operations',
  qualityHandover: 'operations',
  qaChecklists: 'operations',
  snagging: 'operations',
  sampleApprovals: 'operations',
  otherSiteRecords: 'operations',
  rfis: 'operations',
  deliveryNotes: 'operations',
  labourPlant: 'operations',
  operativeInductions: 'operations',
  correspondence: 'communications',
  clientCorrespondence: 'communications',
  mainContractorCorrespondence: 'communications',
  supplyChainCorrespondence: 'communications',
  internalNotes: 'communications',
  meetings: 'communications',
  preStartMeetings: 'communications',
  progressMeetings: 'communications',
  commercialMeetings: 'communications',
  otherMeetings: 'communications',
  finalAccount: 'closeout',
  draftSubmissions: 'closeout',
  negotiations: 'closeout',
  finalAgreement: 'closeout',
  settlementDischarge: 'closeout',
  handoverCompletion: 'closeout',
  completionCertificates: 'closeout',
  warranties: 'closeout',
  omManuals: 'closeout',
  lessonsLearned: 'closeout',
  noticesReferrals: 'disputeResolution',
  submissionsPleadings: 'disputeResolution',
  evidenceSupporting: 'disputeResolution',
  adjudicatorDecision: 'disputeResolution',
  settlementAgreements: 'disputeResolution',
  draftsWorkingPapers: 'internalConfidential',
  internalReviews: 'internalConfidential',
  strategicPlanning: 'internalConfidential',
  legalAdvice: 'internalConfidential',
};

export { FOLDER_NAME_TO_KEY } from './documentFolderNames';

const CACHE_PREFIX = 'document-folders:';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes — stale data shown, revalidated in background

// --- Persistent file-list cache (stale-while-revalidate) ---
// Survives page reloads so folder content appears instantly on revisit.
const FILE_CACHE_PREFIX = 'document-files:';
const FILE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

export const saveFileCache = (projectId: string, folderId: string, files: any[]): void => {
  try {
    const payload = JSON.stringify({ timestamp: Date.now(), data: files });
    localStorage.setItem(`${FILE_CACHE_PREFIX}${projectId}:${folderId}`, payload);
  } catch {
    // Quota exceeded or private mode — silent fail
  }
};

export const loadFileCache = (projectId: string, folderId: string): any[] | null => {
  try {
    const raw = localStorage.getItem(`${FILE_CACHE_PREFIX}${projectId}:${folderId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { timestamp: number; data: any[] };
    if (!parsed?.timestamp || !Array.isArray(parsed.data)) return null;
    if (Date.now() - parsed.timestamp > FILE_CACHE_TTL_MS) {
      localStorage.removeItem(`${FILE_CACHE_PREFIX}${projectId}:${folderId}`);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
};

export const clearFileCache = (projectId: string, folderId?: string): void => {
  try {
    if (folderId) {
      localStorage.removeItem(`${FILE_CACHE_PREFIX}${projectId}:${folderId}`);
    } else {
      const prefix = `${FILE_CACHE_PREFIX}${projectId}:`;
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) toRemove.push(key);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    }
  } catch {
    // Silent fail
  }
};
// Listings rendered from persistent storage must retain their pagination identity.
// The authenticated role/user and API origin are supplied by the caller as scope.
export interface PersistedDocumentListing {
  version: 1;
  timestamp: number;
  data: GoogleDriveFile[];
  nextPageToken: string | null;
  complete: boolean;
  serverPaginationActive: boolean;
}
const scopedListingPrefix = (scope: string) => `document-listings:v1:${encodeURIComponent(scope)}:`;
export const saveDocumentListing = (scope: string, folderId: string, entry: Omit<PersistedDocumentListing, 'version'>): void => {
  try { localStorage.setItem(`${scopedListingPrefix(scope)}${folderId}`, JSON.stringify({ ...entry, version: 1 })); } catch { /* Cache is optional. */ }
};
export const loadDocumentListing = (scope: string, folderId: string): PersistedDocumentListing | null => {
  try {
    const raw = localStorage.getItem(`${scopedListingPrefix(scope)}${folderId}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as PersistedDocumentListing;
    if (entry.version !== 1 || !Array.isArray(entry.data) || !Number.isFinite(entry.timestamp)
      || Date.now() - entry.timestamp > FILE_CACHE_TTL_MS || entry.timestamp > Date.now()
      || typeof entry.complete !== 'boolean' || typeof entry.serverPaginationActive !== 'boolean'
      || !(entry.nextPageToken === null || typeof entry.nextPageToken === 'string')
      || (entry.complete && entry.nextPageToken !== null)) return null;
    return entry;
  } catch { return null; }
};
export const clearDocumentListings = (scope: string, folderId?: string): void => {
  try {
    const prefix = scopedListingPrefix(scope);
    if (folderId) { localStorage.removeItem(`${prefix}${folderId}`); return; }
    const keys = Object.keys(localStorage).filter(key => key.startsWith(prefix));
    keys.forEach(key => localStorage.removeItem(key));
  } catch { /* Cache is optional. */ }
};
// --- end file cache ---

export const saveFolderCache = (cacheKey: string, data: any) => {
  try {
    const payload = JSON.stringify({ timestamp: Date.now(), data });
    localStorage.setItem(`${CACHE_PREFIX}${cacheKey}`, payload);
  } catch {
    // Ignore cache write failures (private mode, quota, etc.)
  }
};

export const loadFolderCache = (cacheKey: string, ttlMs: number = CACHE_TTL_MS) => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { timestamp: number; data: any };
    if (!parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > ttlMs) {
      localStorage.removeItem(`${CACHE_PREFIX}${cacheKey}`);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
};

/**
 * Fetches a file's bytes and hands them to `saveFile` (the caller's blob-save
 * routine) instead of navigating the page to the file URL directly.
 *
 * `window.location.assign(fileUrl)` has no recovery path: once the browser
 * commits to the navigation, an expired link, a dropped connection, or a
 * client-side blocker (ad blocker / corporate content filter throwing
 * ERR_BLOCKED_BY_CLIENT — see #1115) all strand the user on the browser's own
 * error page, outside the app. Fetching first keeps any failure inside a
 * catchable promise so the caller can show a toast and leave the app in place.
 */
export const downloadFileFromUrl = async (
  url: string,
  filename: string,
  saveFile: (blob: Blob, filename: string, ext: string) => Promise<void>,
  fetchImpl: typeof fetch = fetch
): Promise<void> => {
  const response = await fetchImpl(url);
  if (!response.ok) {
    throw new Error(`Failed to download ${filename} (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error(`Downloaded file "${filename}" is empty`);
  }
  const ext = filename.split('.').pop()?.toLowerCase() || 'bin';
  await saveFile(blob, filename, ext);
};

export const fetchWithTimeout = async (
  url: string,
  options: RequestInit = {},
  timeoutMs: number = 30000
) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener(
        'abort',
        () => controller.abort(),
        { once: true }
      );
    }
  }

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Above this size a file is sent as a raw streamed body rather than multipart.
 *
 * The server parses a multipart body with `c.req.formData()`, which holds the
 * entire file in an Edge Function worker that only has ~256MB. Overrunning it
 * does not fail just that upload: the runtime kills the worker and every other
 * request the instance was serving dies with a CORS-header-less 502. Keep this
 * comfortably below the server's own MAX_BUFFERED_UPLOAD_MB.
 */
export const STREAMING_UPLOAD_THRESHOLD_BYTES = 32 * 1024 * 1024;
const uploadCommandIds = new WeakMap<File, string>();

/** The non-file fields every /project-folders/upload call sends. */
export interface ProjectUploadFields {
  folderId: string;
  userId: string;
  projectId: string;
  folderType: string;
  documentControlMetadata?: unknown;
  /** Stable for every retry of one logical upload. */
  uploadCommandId?: string;
}

/**
 * Build the URL and body for a /project-folders/upload call, picking the
 * multipart or streaming shape by file size. Streaming moves the metadata into
 * query params because the body is reserved for the file's bytes.
 */
export const buildProjectUploadRequest = (
  baseUrl: string,
  file: File,
  fields: ProjectUploadFields,
): { url: string; body: FormData | File } => {
  const metadata = fields.documentControlMetadata
    ? JSON.stringify(fields.documentControlMetadata)
    : null;
  let uploadCommandId = fields.uploadCommandId || uploadCommandIds.get(file);
  if (!uploadCommandId) {
    uploadCommandId = crypto.randomUUID();
    uploadCommandIds.set(file, uploadCommandId);
  }

  if (file.size <= STREAMING_UPLOAD_THRESHOLD_BYTES) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', fields.folderId);
    formData.append('userId', fields.userId);
    for (const [name, value] of Object.entries(documentScopeAdapter(fields.projectId).fields())) formData.append(name, value);
    formData.append('folderType', fields.folderType);
    formData.append('uploadCommandId', uploadCommandId);
    if (metadata) formData.append('documentControlMetadata', metadata);
    return { url: baseUrl, body: formData };
  }

  const params = new URLSearchParams({
    stream: '1',
    fileName: file.name,
    // Browsers leave `type` empty for extensions they do not know, .ifc among
    // them. Say octet-stream explicitly rather than let the server guess.
    fileType: file.type || 'application/octet-stream',
    folderId: fields.folderId,
    folderType: fields.folderType,
    uploadCommandId,
  });
  documentScopeAdapter(fields.projectId).applyTo(params);
  if (metadata) params.set('documentControlMetadata', metadata);
  return { url: `${baseUrl}?${params.toString()}`, body: file };
};

/**
 * XHR-based upload that fires real-time byte-level progress events.
 * The Fetch API has no upload progress — only XHR exposes xhr.upload.onprogress.
 *
 * @param url        - endpoint
 * @param body       - multipart FormData, or the File itself for streamed uploads
 * @param headers    - request headers (do NOT include Content-Type; XHR sets it from the body)
 * @param onProgress - called with (bytesSent, bytesTotal) as data is transmitted
 * @param timeoutMs  - hard abort deadline (default 120 s)
 */
export const uploadWithProgress = (
  url: string,
  body: FormData | File | Blob,
  headers: Record<string, string>,
  onProgress: (bytesSent: number, bytesTotal: number) => void,
  timeoutMs = 120_000,
  signal?: AbortSignal,
): Promise<{ ok: boolean; status: number; json: () => Promise<any> }> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new Error('Upload aborted')); return; }
    const xhr = new XMLHttpRequest();
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    const timeoutId = setTimeout(() => {
      xhr.abort();
      reject(new Error('Upload timeout - file may be too large or slow network.'));
    }, timeoutMs);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      clearTimeout(timeoutId);
      const body = xhr.responseText;
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => {
          try {
            return Promise.resolve(JSON.parse(body));
          } catch {
            return Promise.reject(new Error('Invalid JSON response'));
          }
        },
      });
    };

    xhr.onerror = () => {
      clearTimeout(timeoutId);
      reject(new TypeError('Failed to fetch'));
    };

    xhr.onabort = () => {
      clearTimeout(timeoutId);
      reject(new Error('Upload aborted'));
    };

    xhr.open('POST', url);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.send(body);
  });
};

/** OS metadata files that folder drops sweep up but nobody wants in a project. */
const JUNK_FILE_NAMES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

/**
 * Expand a drop's DataTransfer into real File objects, descending into
 * dropped directories. `dataTransfer.files` represents a dropped folder as a
 * single directory File the browser cannot read — sending it dies at the
 * network layer with net::ERR_ACCESS_DENIED before any HTTP response — so
 * folders must be walked via webkitGetAsEntry() instead. Files found inside
 * folders get webkitRelativePath defined to match the format
 * <input webkitdirectory> produces, so the upload queue can rebuild the
 * subfolder tree.
 *
 * Must be CALLED synchronously from the drop handler — DataTransferItems are
 * neutered once the handler yields to the event loop, so the entry snapshot
 * below runs before the first await.
 */
export const collectFilesFromDataTransfer = (
  dataTransfer: DataTransfer
): Promise<File[]> => {
  const items = Array.from(dataTransfer.items || []);
  if (items.length === 0 || typeof items[0].webkitGetAsEntry !== 'function') {
    return Promise.resolve(Array.from(dataTransfer.files));
  }

  const entries = items
    .map((item) => (item.kind === 'file' ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) {
    return Promise.resolve(Array.from(dataTransfer.files));
  }

  // readEntries returns at most 100 entries per call — drain until empty.
  const readAllEntries = (reader: FileSystemDirectoryReader) =>
    new Promise<FileSystemEntry[]>((resolve, reject) => {
      const collected: FileSystemEntry[] = [];
      const readBatch = () =>
        reader.readEntries((batch) => {
          if (batch.length === 0) return resolve(collected);
          collected.push(...batch);
          readBatch();
        }, reject);
      readBatch();
    });

  const entryToFile = (entry: FileSystemFileEntry) =>
    new Promise<File>((resolve, reject) => entry.file(resolve, reject));

  const collect = async () => {
    const files: File[] = [];

    const walk = async (entry: FileSystemEntry): Promise<void> => {
      if (entry.isFile) {
        if (JUNK_FILE_NAMES.has(entry.name)) return;
        let file: File;
        try {
          file = await entryToFile(entry as FileSystemFileEntry);
        } catch (err) {
          // Unreadable on disk (cloud placeholder, permissions) — skip it
          // rather than sink the whole folder.
          console.warn(`Skipping unreadable file: ${entry.fullPath}`, err);
          return;
        }
        // fullPath is "/Folder/sub/name.ext"; strip the leading slash. A
        // loose top-level file resolves to just its name — leave it with no
        // relative path so it lands in the drop target folder itself.
        const relativePath = entry.fullPath.replace(/^\//, '');
        if (relativePath !== file.name) {
          Object.defineProperty(file, 'webkitRelativePath', {
            value: relativePath,
          });
        }
        files.push(file);
        return;
      }
      if (entry.isDirectory) {
        let children: FileSystemEntry[];
        try {
          children = await readAllEntries(
            (entry as FileSystemDirectoryEntry).createReader()
          );
        } catch (err) {
          // Same isolation as unreadable files — a permission-denied
          // subfolder must not sink the rest of the drop.
          console.warn(`Skipping unreadable directory: ${entry.fullPath}`, err);
          return;
        }
        for (const child of children) await walk(child);
      }
    };

    for (const entry of entries) await walk(entry);
    return files;
  };

  return collect();
};

export const batchPromises = async <T, R>(
  items: T[],
  batchSize: number,
  factory: (item: T) => Promise<R>
) => {
  const results: R[] = [];
  const safeBatchSize = Math.max(1, batchSize || 1);
  for (let i = 0; i < items.length; i += safeBatchSize) {
    const batch = items.slice(i, i + safeBatchSize);
    const batchResults = await Promise.all(batch.map(factory));
    results.push(...batchResults);
  }
  return results;
};

export const collectFolderIds = (folders: any[]) => {
  const ids: string[] = [];
  const stack = [...(folders || [])];
  while (stack.length > 0) {
    const folder = stack.pop();
    if (!folder) continue;
    if (folder.id) ids.push(folder.id);
    if (folder.subfolders?.length) stack.push(...folder.subfolders);
    if (folder.children?.length) stack.push(...folder.children);
  }
  return ids;
};

export const getFileIcon = (mimeType: string) => {
  const type = mimeType?.toLowerCase() || '';
  const iconClassName = 'w-5 h-5';
  const icon = (Component: React.ComponentType<{ className?: string }>) =>
    React.createElement(Component, { className: iconClassName });

  if (type === FOLDER_MIME_TYPE) {
    return icon(FolderOpen);
  }
  if (type.includes('image')) return icon(Image);
  if (type.includes('audio')) return icon(FileAudio);
  if (type.includes('video')) return icon(FileVideo);
  if (
    type.includes('zip') ||
    type.includes('compressed') ||
    type.includes('rar') ||
    type.includes('7z') ||
    type.includes('tar')
  ) {
    return icon(FileArchive);
  }
  if (
    type.includes('pdf') ||
    type.includes('document') ||
    type.includes('text')
  ) {
    return icon(FileText);
  }
  if (
    type.includes('spreadsheet') ||
    type.includes('excel') ||
    type.includes('sheet') ||
    type.includes('csv') ||
    type.includes('tsv')
  ) {
    return icon(FileSpreadsheet);
  }
  if (
    type.includes('json') ||
    type.includes('xml') ||
    type.includes('yaml') ||
    type.includes('csv') ||
    type.includes('code')
  ) {
    return icon(FileCode);
  }
  return icon(File);
};


export { formatFileSize, getFileType } from '../../shared-ui/documents/documentRowModel';

/** A revision that document control has marked as replaced. */
export const isSupersededDocument = (doc: GoogleDriveFile): boolean => {
  const metadata = doc.documentControl || {};
  return metadata.reviewState === 'superseded' || Boolean(metadata.supersededByDriveFileId);
};

/** The revision a document link should open when several files share a
 * name: the current one, and among equals the most recently issued, so a
 * link to a revised drawing never lands on the version it replaced. */
export const pickCurrentRevision = (matches: readonly GoogleDriveFile[]): GoogleDriveFile | null => {
  if (matches.length === 0) return null;
  const issuedAt = (doc: GoogleDriveFile) =>
    new Date(doc.documentControl?.issueDate || doc.modifiedTime || 0).getTime();
  return [...matches].sort((a, b) => {
    const aCurrent = !isSupersededDocument(a);
    const bCurrent = !isSupersededDocument(b);
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
    return issuedAt(b) - issuedAt(a);
  })[0];
};
